import * as React from 'react';

import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import SubmitIcon from '@mui/icons-material/Publish';
import ApproveIcon from '@mui/icons-material/DoneAll';
import { yupResolver } from '@hookform/resolvers/yup';

import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from 'notistack';

import ControlledForm from '../../components/ControlledForm';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import ScrollToTop from '../../components/ScrollToTop';

import FormReadOnlySection from '../../components/FormReadOnlySection';
import FormPaper from '../../components/FormPaper';
import FormTitle from '../../components/FormTitle';
import FormHeader from '../../components/FormHeader';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { useSafetyEventReportData } from '../../core/context/SafetyEventReportDataContext';
import useDeleteSafetyEventDraft from '../../core/api/hooks/safety-event/useDeleteSafetyEventDraft';
import { EventError, EventFormStatus, SafetyEventReport } from '../../core/api/clients';

import EventStep from './FormSteps/EventStep';

import '../../core/styles/disabledFields.css';
import FlightStep from './FormSteps/FlightStep';
import FormStatus from '../../components/FormStatus';

import SafetyEventFormValidationSchema from './SafetyEventFormValidationSchema';
import { FormValidationProvider } from '../../core/context/FormValidationContext';
import queryKeys from '../../core/api/queryKeys';
import CrewDebriefStep from './FormSteps/CrewDebriefStep';

function SafetyEventFormView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authorizedFormData = useAuthorizedFormData();
  const { enqueueSnackbar } = useSnackbar();

  const {
    safetyEventReport,
    update,
    submit,
    allowedActions,
    submitStatus,
  } = useSafetyEventReportData();

  const { mutate: deleteSafetyEventDraft } = useDeleteSafetyEventDraft();
  const [confDialogOpen, setConfDialogOpen] = React.useState<boolean>(false);
  const [notifyPartner, setNotifyPartner] = React.useState<boolean>(true);
  const formMethods = useForm<SafetyEventReport>({
    defaultValues: safetyEventReport,
    mode: 'onBlur',
    resolver: yupResolver(SafetyEventFormValidationSchema),
  });

  React.useEffect(() => {
    formMethods.reset(safetyEventReport);
  }, [safetyEventReport]);

  const submitCallback = () => {
    Promise.all([
      queryClient.refetchQueries({ queryKey: [queryKeys.SafetyEvent.Drafts] }),
      queryClient.refetchQueries({ queryKey: [queryKeys.SafetyEvent.Submissions] }),
    ]).then(() => {
      navigate('/');
    });
  };

  const handleUpdate = () => {
    update(formMethods.getValues());
  };

  const handleSubmit = () => {
    submit(safetyEventReport.id, submitCallback);
  };

  const approveCallback = (eventReport: SafetyEventReport) => {
    // setting timeout for navigating allows refetch to finish
    // and display updated data in the approval list
    queryClient
      .refetchQueries({ queryKey: [queryKeys.SafetyEvent.ApprovalList] })
      .then(() => {
        navigate('/form/safety-event', {
          state: {
            isError: false,
            title: 'The report was successfully approved',
            message: 'The report was successfully approved',
          },
        });
      });
  };

  const handleDelete = () => {
    if (safetyEventReport.id) {
      deleteSafetyEventDraft(safetyEventReport.id, {
        onSuccess: () => {
          queryClient
            .refetchQueries({ queryKey: [queryKeys.SafetyEvent.ApprovalList] })
            .then(() => {
              navigate('/form/safety-event');
            });
        },
        onError: () => enqueueSnackbar('Error deleting form', { variant: 'error' }),
      });
    }
  };
  const openDeleteConfirmationDialog = () => {
    setConfDialogOpen(true);
  };
  const handleIsConfidential = () => {
    setNotifyPartner(!notifyPartner);
  };

  return (
    <FormPaper color={authorizedFormData.metadata?.color}>
      <ScrollToTop />
      <ConfirmationDialog isOpen={confDialogOpen} setOpen={setConfDialogOpen} confirm={handleDelete} />
      <FormHeader>
        <FormTitle
          title="Safety Event Report"
          subtitle={[...[[
            'Created By:',
            safetyEventReport.employeeName,
            `[${safetyEventReport.employeeId}],`,
            safetyEventReport.employeeLocation,
            safetyEventReport.employeeTitle,
          ].join(' ')], safetyEventReport.submittedById == null ? ''
            : ['Submitted By:',
              safetyEventReport.submittedByName,
              `[${safetyEventReport.submittedById}],`,
              safetyEventReport.submittedByLocation,
              safetyEventReport.submittedByTitle].join(' '),
          ]}
        />
        <FormStatus
          status={safetyEventReport.status}
          subtext={undefined}
        />
        <Box marginLeft={1} marginRight={1}>
          <Chip label="Audit Logs" variant="outlined" color="primary" onClick={() => navigate(`/form/safety-event/${safetyEventReport.id}/logs`)} />
        </Box>
      </FormHeader>
      <ControlledForm onUpdate={handleUpdate} enableUpdate>
        <FormProvider {...formMethods}>
          <FormValidationProvider<SafetyEventReport>>
            <Stack spacing={2}>
              <FormReadOnlySection title="Flight & Crew">
                <FlightStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Event">
                <EventStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Crew Debrief">
                <CrewDebriefStep visible readOnly />
              </FormReadOnlySection>
            </Stack>
          </FormValidationProvider>
        </FormProvider>
      </ControlledForm>
      <Box display="flex" sx={{ my: 2 }}>
        <Button
          sx={{ marginRight: 2 }}
          onClick={() => navigate('/form/safety-event')}
        >
          Close
        </Button>
        {allowedActions?.delete && (
          <Button
            sx={{ marginRight: 2 }}
            color="error"
            variant="outlined"
            onClick={openDeleteConfirmationDialog}
          >
            Delete
          </Button>
        )}
        <Box flexGrow={1} />
        {allowedActions?.submit && safetyEventReport.status !== EventFormStatus.Submitted && (
          <LoadingButton
            loading={submitStatus === 'pending'}
            loadingPosition="end"
            endIcon={<SubmitIcon />}
            sx={{ marginRight: 2 }}
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={formMethods.formState.isDirty}
          >
            {`${formMethods.formState.isDirty ? 'Submit (Unsaved)' : 'Submit'}`}
          </LoadingButton>
        )}
        {allowedActions?.edit && (
          <Button
            sx={{ marginRight: 2 }}
            color="primary"
            variant="outlined"
            onClick={() => navigate(`/form/safety-event/${safetyEventReport.id}/edit`)}
          >
            Edit
          </Button>
        )}
      </Box>
    </FormPaper>
  );
}

export default SafetyEventFormView;
